import React, { FC, MouseEvent, KeyboardEvent, useState } from 'react';
import { useDrag } from 'react-dnd';
import ReactTooltip from 'react-tooltip';
import _truncate from 'lodash/truncate';

import handleKeyPress from '../../../common/handleKeyPress';
import { populateThirdPartyBlock } from '../../../common/populateThirdPartyBlock';
import { IBlock } from '../../../types/content';
import { ISBPost } from '../../../types/thirdPartyContent';
import * as itemTypes from '../../../constants/itemTypes/itemTypes';
import * as Styled from './styles';
import imageSources from '../../../constants/imageSources/imageSources';

interface IProps {
    post: ISBPost;
    block: IBlock;
    currentLanguage: string;
}

const PostPreview: FC<IProps> = ({ post, block, currentLanguage }) => {
    const {
        index: blockIndex,
        content: {
            columns: [{ columnIdx }],
        },
    } = block;
    const [language, setLanguage] = useState(localStorage.getItem('sb-selected-language') || currentLanguage);

    const languages = Object.keys(post.contents).map((lang) => {
        const [langCode, country] = lang.split('_');
        return {
            lang,
            display: langCode === 'pt' ? `${langCode.toUpperCase()}-${country.toUpperCase()}` : langCode.toUpperCase(),
            selected: language === lang,
        };
    });
    const noSelectedLanguage = languages.every(({ selected }) => !selected);
    if (noSelectedLanguage) {
        languages[0].selected = true;
    }

    const contents = post.contents[language as keyof typeof post.contents] || post.contents[languages[0].lang];
    const channel = post.channels[language] || post.channels[Object.keys(post.channels)[0]];
    const truncatedText = _truncate(contents.title, { length: 45 });
    const imageSource = contents.image || imageSources.STAFFBASE.NULL_IMAGE;
    const { id, published, updated, author, postUrl } = post;

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: itemTypes.STAFFBASE_POST,
            item: () => {
                ReactTooltip.hide();
                return { contents: { ...contents, postUrl, language } };
            },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [contents]
    );

    const handleLanguageClick = (event: MouseEvent | KeyboardEvent, lang: string) => {
        event.preventDefault();
        event.stopPropagation();
        setLanguage(lang);
    };

    const populateBlock = () => {
        populateThirdPartyBlock({
            blockIndex,
            columnIdx: columnIdx as string,
            contents: { ...contents, postUrl, language },
        });
    };

    return (
        <>
            <Styled.PostCard
                data-tip
                data-for={`post-${id}`}
                onClick={populateBlock}
                onKeyDown={(e) => handleKeyPress(e, populateBlock)}
                ref={drag}
                isDragging={isDragging}
            >
                <Styled.PostCardHeader>
                    <Styled.ChannelName>{channel}</Styled.ChannelName>
                    <Styled.PublishedDate>{published}</Styled.PublishedDate>
                </Styled.PostCardHeader>
                {languages.length > 1 && (
                    <Styled.Languages>
                        {languages.map(({ display, selected, lang }) => (
                            <div
                                key={`${lang}-${id}`}
                                onClick={(e) => handleLanguageClick(e, lang)}
                                onKeyDown={(e) => handleKeyPress(e, () => handleLanguageClick(e, lang))}
                                style={{ padding: '0 4px', cursor: 'pointer' }}
                                role="button"
                                tabIndex={0}
                            >
                                <Styled.Language selected={selected}>{display}</Styled.Language>{' '}
                            </div>
                        ))}
                    </Styled.Languages>
                )}
                <Styled.PostCardBody>
                    <Styled.PreviewTitle>{truncatedText}</Styled.PreviewTitle>
                    <Styled.ThumbnailImage src={imageSource} />
                </Styled.PostCardBody>
            </Styled.PostCard>
            <Styled.PostReactTooltip id={`post-${id}`} place="left" effect="solid">
                <Styled.PostTooltipDiv>
                    <Styled.PostTooltipText>
                        <b>Published:</b> {published}
                    </Styled.PostTooltipText>
                    {updated !== published && (
                        <Styled.PostTooltipText>
                            <b>Updated:</b> {updated}
                        </Styled.PostTooltipText>
                    )}
                    <Styled.PostTooltipText>
                        <b>Author:</b> {author || '-'}
                    </Styled.PostTooltipText>
                    <Styled.PostTooltipText>
                        <br />
                    </Styled.PostTooltipText>
                    <Styled.PostTooltipText>{contents.title}</Styled.PostTooltipText>
                </Styled.PostTooltipDiv>
            </Styled.PostReactTooltip>
        </>
    );
};

export default PostPreview;
